import { tagged } from "@mirage/service-logging";
import {
  CancellableTerminalReason,
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import { cancelable, CancelationError } from "@mirage/shared/util/cancelable";
import * as rx from "rxjs";

const logger = tagged("typeahead/source-helpers");

export function getSourceLimit(
  resultType: typeahead.ResultType,
  query: string,
  mode: typeahead.Mode,
  config: typeahead.Config,
  grossLimit: number,
  sectionLimit?: number,
) {
  if (resultType === typeahead.ResultType.CalendarEvent) {
    return 3;
  }

  if (resultType === typeahead.ResultType.RecentChat) {
    return mode === typeahead.Mode.Ask && !query.length ? 4 : 2;
  }

  if (resultType === typeahead.ResultType.PreviousQuery) {
    if (mode === typeahead.Mode.Find && !query.length) {
      return 4;
    }
    if (query.length && config.isDualModeLauncher) {
      return 1;
    }
    return 2;
  }

  if (resultType === typeahead.ResultType.PeopleFilter && query.length > 0) {
    return config.isDualModeLauncher ? 1 : 2;
  }

  if (
    resultType === typeahead.ResultType.PeopleRecommendation &&
    query.length > 0
  ) {
    return config.isDualModeLauncher ? 1 : 2;
  }

  return sectionLimit ?? grossLimit;
}

export type CancellableSourceConfig = {
  timeoutMs: number;
};

export type CancellableSourceFn<T extends Record<string, unknown>> = (
  query: string,
  mode: typeahead.Mode,
  options: typeahead.TaggedSearchOptions,
  getter: () => Promise<T[]>,
  converter: (allSearchResults: T[]) => typeahead.ScoredResult[],
  emitter: (
    reason: CancellableTerminalReason,
    results?: typeahead.ScoredResult[],
  ) => typeahead.ScoredResult[],
  config: CancellableSourceConfig,
) => rx.Observable<typeahead.ScoredResult[]>;

export function cancellableSourceFactory<
  T extends Record<string, unknown>,
>(): CancellableSourceFn<T> {
  // These get created at the module level and are not tied to a pipeline run
  let activeQuery: string | null = null;
  let activeCancelFn: (() => void) | null = null;

  return function (
    query,
    _mode,
    { pipelineUuid },
    getter,
    converter,
    emitter,
    { timeoutMs },
  ) {
    if (activeCancelFn && activeQuery !== query) {
      activeCancelFn();
    }

    activeQuery = query;

    return new rx.Observable<typeahead.ScoredResult[]>((subscriber) => {
      let cancelled = false;

      const { promise: apiPromise, cancel } = cancelable(getter());

      activeCancelFn = () => {
        cancelled = true;
        cancel();
      };

      const timeoutHandle = setTimeout(() => {
        if (!cancelled) {
          logger.info(
            `Typeahead live result timeout triggered for pipeline=${pipelineUuid} and source=${SourceId.Extensions}`,
          );
          cancelled = true;
          cancel();
          subscriber.next(emitter("timeout"));
          subscriber.complete();
        }
      }, timeoutMs);

      apiPromise
        .then((allSearchResults) => {
          if (cancelled || activeQuery !== query) {
            return;
          }

          clearTimeout(timeoutHandle);

          const scoredResults = converter(allSearchResults);

          subscriber.next(emitter("success", scoredResults));
          subscriber.complete();
        })
        .catch((error) => {
          if (error instanceof CancelationError) return;

          if (cancelled || activeQuery !== query) {
            return;
          }

          clearTimeout(timeoutHandle);
          subscriber.next(emitter("error"));
          subscriber.complete();
        });

      return () => {
        cancelled = true;
        clearTimeout(timeoutHandle);
        cancel();

        if (activeQuery === query) {
          activeQuery = null;
          activeCancelFn = null;
        }
      };
    });
  };
}
